/* eslint-disable import/no-duplicates */
/* eslint-disable import/order */
/* eslint-disable import/namespace */
/* eslint-disable import/export */
/**
 * Schemas de validación con Zod
 *
 * ¿Por qué Zod?
 * 1. Infiere tipos automáticamente (sin duplicar interfaces)
 * 2. Validación en runtime (útil para datos externos)
 * 3. Mensajes de error personalizables
 * 4. Composable (combinar schemas)
 *
 * Uso:
 *   import { transferSchema, TransferFormData } from '@tankef/core/schemas';
 *
 *   // Validar datos
 *   const result = transferSchema.safeParse(formData);
 *   if (!result.success) {
 *     console.log(result.error.issues);
 *   }
 *
 *   // El tipo se infiere automáticamente
 *   const data: TransferFormData = result.data;
 */
import * as zod from 'zod';

// ═══════════════════════════════════════════════════════════════
// VALIDADORES MEXICANOS
// Schemas reutilizables para datos específicos de México
// ═══════════════════════════════════════════════════════════════

/**
 * CLABE Interbancaria (18 dígitos)
 * Estructura: BBB-PPP-CCCCCCCCCCC-D
 * - BBB: Código de banco (3 dígitos)
 * - PPP: Código de plaza (3 dígitos)
 * - C: Número de cuenta (11 dígitos)
 * - D: Dígito verificador (1 dígito)
 */
export const clabeSchema = zod.z
  .string()
  .length(18, 'La CLABE debe tener 18 dígitos')
  .regex(/^\d+$/, 'La CLABE solo debe contener números');

/**
 * CURP (18 caracteres)
 * Clave Única de Registro de Población
 * Formato: [L][AEIOUX][LL][YYMMDD][H/M][LLLLL][L0-9][D]
 * - Pos 1: Letra (apellido paterno)
 * - Pos 2: Vocal o X (apellido paterno)
 * - Pos 3-4: 2 letras (apellido materno)
 * - Pos 5-10: 6 dígitos YYMMDD
 * - Pos 11: H o M (sexo)
 * - Pos 12-16: 5 letras (consonantes internas)
 * - Pos 17: Letra o dígito (folio)
 * - Pos 18: Dígito verificador
 */
export const curpSchema = zod.z
  .string()
  .length(18, 'El CURP debe tener 18 caracteres')
  .regex(/^[A-Z][AEIOUX][A-Z]{2}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/, 'El formato del CURP no es válido');

/**
 * RFC (12-13 caracteres)
 * Registro Federal de Contribuyentes
 * - Personas físicas: 13 caracteres
 * - Personas morales: 12 caracteres
 */
export const rfcSchema = zod.z
  .string()
  .min(12, 'El RFC debe tener al menos 12 caracteres')
  .max(13, 'El RFC no puede tener más de 13 caracteres')
  .regex(/^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/, 'El formato del RFC no es válido');

/**
 * Teléfono mexicano (10 dígitos)
 * Sin código de país, sin guiones ni espacios
 */
export const phoneSchema = zod.z
  .string()
  .length(10, 'El teléfono debe tener 10 dígitos')
  .regex(/^\d+$/, 'El teléfono solo debe contener números');

/**
 * Monto de dinero
 * - Positivo
 * - Máximo 2 decimales
 */
export const moneySchema = zod.z
  .number()
  .positive('El monto debe ser mayor a cero')
  .multipleOf(0.01, 'El monto no puede tener más de 2 decimales');

// ═══════════════════════════════════════════════════════════════
// SCHEMAS BÁSICOS
// ═══════════════════════════════════════════════════════════════

export const emailSchema = zod.z
  .string()
  .min(1, 'El correo es requerido')
  .email('Ingresa un correo válido');

// Password rules live in primitives/password.ts so FE and BE can share them.
import {
  passwordSchema,
  PASSWORD_RULES,
  PASSWORD_REGEX,
  PASSWORD_RULE_REGEXES,
  checkPasswordRules,
  estimatePasswordStrengthHeuristic,
  type PasswordStrength,
} from './primitives/password';

export {
  passwordSchema,
  PASSWORD_RULES,
  PASSWORD_REGEX,
  PASSWORD_RULE_REGEXES,
  checkPasswordRules,
  estimatePasswordStrengthHeuristic,
};
export type { PasswordStrength };

// ═══════════════════════════════════════════════════════════════
// SCHEMAS DE FORMULARIOS
// ═══════════════════════════════════════════════════════════════

/**
 * Login
 */
export const loginSchema = zod.z.object({
  email: emailSchema,
  password: zod.z.string().min(1, 'La contraseña es requerida'),
  rememberMe: zod.z.boolean().optional(),
});

/**
 * Registro
 */
export const registerSchema = zod.z
  .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: zod.z.string(),
    phone: phoneSchema,
    acceptTerms: zod.z.literal(true, {
      errorMap: () => ({ message: 'Debes aceptar los términos y condiciones' }),
    }),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Las contraseñas no coinciden',
    path: ['confirmPassword'],
  });

/**
 * Transferencia SPEI
 */
export const transferSchema = zod.z.object({
  destinationClabe: clabeSchema,
  amount: moneySchema.max(500000, 'El monto máximo por transferencia es $500,000'),
  concept: zod.z
    .string()
    .min(1, 'El concepto es requerido')
    .max(40, 'El concepto no puede exceder 40 caracteres'),
  reference: zod.z
    .string()
    .max(7, 'La referencia no puede exceder 7 dígitos')
    .regex(/^\d*$/, 'La referencia solo puede contener números')
    .optional(),
});

/**
 * Beneficiario
 */
export const beneficiarySchema = zod.z.object({
  alias: zod.z
    .string()
    .min(1, 'El alias es requerido')
    .max(50, 'El alias no puede exceder 50 caracteres'),
  clabe: clabeSchema,
  bankName: zod.z.string().optional(),
  email: emailSchema.optional().or(zod.z.literal('')),
  isFavorite: zod.z.boolean().optional(),
});

/**
 * Código MFA
 */
export const mfaSchema = zod.z.object({
  code: zod.z
    .string()
    .length(6, 'El código debe tener 6 dígitos')
    .regex(/^\d+$/, 'El código solo debe contener números'),
});

// ═══════════════════════════════════════════════════════════════
// TIPOS INFERIDOS
// Estos tipos se generan automáticamente del schema
// ═══════════════════════════════════════════════════════════════

export type LoginFormData = zod.z.infer<typeof loginSchema>;
export type RegisterFormData = zod.z.infer<typeof registerSchema>;
export type TransferFormData = zod.z.infer<typeof transferSchema>;
export type BeneficiaryFormData = zod.z.infer<typeof beneficiarySchema>;
export type MFAFormData = zod.z.infer<typeof mfaSchema>;

// ═══════════════════════════════════════════════════════════════
// REGISTRO MULTI-PASO
// Schemas y tipos para el flujo de registro completo
// ═══════════════════════════════════════════════════════════════

/**
 * Pasos del registro
 */
export const RegistrationSteps = {
  LOCATION: 'location',
  EMAIL: 'email',
  PASSWORD: 'password',
  PHONE: 'phone',
  IDENTITY: 'identity',
  COMPLETE: 'complete',
} as const;

export type RegistrationStep = (typeof RegistrationSteps)[keyof typeof RegistrationSteps];

/**
 * Paso 1: Ubicación
 */
export const locationStepSchema = zod.z.object({
  latitude: zod.z.number(),
  longitude: zod.z.number(),
  address: zod.z.string().optional(),
  city: zod.z.string().optional(),
  state: zod.z.string().optional(),
  country: zod.z.string().optional(),
  isInMexico: zod.z.boolean(),
});

/**
 * Paso 2: Email
 */
export const emailStepSchema = zod.z.object({
  email: emailSchema,
  emailVerified: zod.z.boolean(),
  emailVerificationCode: zod.z.string().length(6).optional(),
});

/**
 * Paso 3: Teléfono
 */
export const phoneStepSchema = zod.z.object({
  phone: phoneSchema,
  phoneVerified: zod.z.boolean(),
  phoneVerificationCode: zod.z.string().length(6).optional(),
});

/**
 * Paso 4: Identidad
 */
export const identityStepSchema = zod.z.object({
  identityVerified: zod.z.boolean(),
  idFrontImage: zod.z.string().optional(),
  idBackImage: zod.z.string().optional(),
  selfieImage: zod.z.string().optional(),
});

/**
 * Datos personales (recopilados durante el registro)
 */
export const personalDataSchema = zod.z.object({
  nombres: zod.z.string().min(1, 'El nombre es requerido'),
  apellidoPaterno: zod.z.string().min(1, 'El apellido paterno es requerido'),
  apellidoMaterno: zod.z.string().optional(),
  fechaNacimiento: zod.z.string().optional(),
  genero: zod.z.enum(['M', 'F', 'O']).optional(),
  nacionalidad: zod.z.string().optional(),
});

/**
 * Schema completo del formulario de registro multi-paso
 */
export const registrationFormSchema = zod.z.object({
  // Step tracking
  currentStep: zod.z.enum(['location', 'email', 'phone', 'identity', 'complete']),
  completedSteps: zod.z.array(zod.z.enum(['location', 'email', 'phone', 'identity'])),

  // Location (Step 1)
  location: locationStepSchema.optional(),

  // Email (Step 2)
  email: zod.z.string().optional(),
  emailVerified: zod.z.boolean().default(false),

  // Phone (Step 3)
  phone: zod.z.string().optional(),
  phoneVerified: zod.z.boolean().default(false),

  // Identity (Step 4)
  identityVerified: zod.z.boolean().default(false),

  // Personal data
  personalData: personalDataSchema.optional(),
});

/**
 * Tipos inferidos para registro multi-paso
 */
export type LocationStepData = zod.z.infer<typeof locationStepSchema>;
export type EmailStepData = zod.z.infer<typeof emailStepSchema>;
export type PhoneStepData = zod.z.infer<typeof phoneStepSchema>;
export type IdentityStepData = zod.z.infer<typeof identityStepSchema>;
export type PersonalData = zod.z.infer<typeof personalDataSchema>;
export type RegistrationFormData = zod.z.infer<typeof registrationFormSchema>;

/**
 * Schema para verificación de código OTP
 */
export const otpVerificationSchema = zod.z.object({
  code: zod.z
    .string()
    .length(6, 'El código debe tener 6 dígitos')
    .regex(/^\d+$/, 'El código solo debe contener números'),
  type: zod.z.enum(['email', 'phone']),
  target: zod.z.string(), // email or phone number
});

export type OTPVerificationData = zod.z.infer<typeof otpVerificationSchema>;

export * from './onboarding.schema';

export * from './kyc.schema';

export * from './company.schema';

export * from './origin-of-funds.schema';

export * from './privacy-notice.schema';

export * from './pep.schema';

export * from './legal-rep.schema';

export * from './account-user.schema';

export * from './fields';
export * from './beneficial-owner-identity.schema';
